import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Swal from 'sweetalert2';
import axios from 'axios';

// Componentes personalizados
import SoftTypography from 'components/SoftTypography';
import SoftBox from 'components/SoftBox';
import Grid from '@mui/material/Grid';
import FormField from 'layouts/applications/wizard/components/FormField';
import SoftButton from 'components/SoftButton';
import SoftDatePicker from 'components/SoftDatePicker';

// Selectores personalizados
import IncotermSelector from 'components/IncotermSelector';
import TransportSelector from 'components/TransportSelector';
import CurrencySelector from 'components/CurrencySelector';
import PackageSelector from 'components/PackageSelector';

const ModalShipment = ({ show, handleClose, handleSave, initialData }) => {
  const [formData, setFormData] = useState({
    descripcion: '',
    fecha_entrega: '',
    tipo_embalaje: '',
    comprador: '',
    tipo_despacho: '',
    pais_origen: '',
    incoterms: '',
    lugar_entrega: '',
    cantidad_unidades: '',
    termino_pago: '',
    tipo_moneda: '',
    precio: '',
    fecharealentrega_orden_detalle: '',
    fechaembarque_orden_detalle: '',
    fechaarribo_orden_detalle: ''
  });

  const [packageOptions, setPackageOptions] = useState([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/maintenance/packaging-types/`)
      .then((response) => {
        const options = response.data.map((item) => ({
          value: item.id_tipo_embalaje,
          label: item.nombre_tipo_embalaje,
        }));
        setPackageOptions(options);
      })
      .catch((err) => {
        console.error('Error al cargar tipos de embalaje:', err);
        setPackageOptions([]);
      });
  }, []);

  // Actualiza formData cada vez que el modal se abra o initialData cambie.
  useEffect(() => {
    console.log("Modal initialData:", initialData);
    // Si initialData es un array, usamos el primer elemento
    let data = initialData;
    if (Array.isArray(initialData)) {
      data = initialData[0];
    }
    if (data) {
      setFormData({
        descripcion: data.des || '',
        fecha_entrega: data.fechaentrega_orden_detalle || '',
        tipo_embalaje: data.package || '',
        comprador: data.com || '',
        tipo_despacho: data.tipo_transporte || '',
        pais_origen: data.pai || '',
        incoterms: data.incoterm || '',
        lugar_entrega: data.lug || '',
        cantidad_unidades: data.can || '',
        termino_pago: data.ter || '',
        tipo_moneda: data.tipo_moneda || '',
        precio: data.pre || '',
        fecharealentrega_orden_detalle: data.fecharealentrega_orden_detalle || '',
        fechaembarque_orden_detalle: data.fechaembarque_orden_detalle || '',
        fechaarribo_orden_detalle: data.fechaarribo_orden_detalle || ''
      });
    } else {
      setFormData({
        descripcion: '',
        fecha_entrega: '',
        tipo_embalaje: '',
        comprador: '',
        tipo_despacho: '',
        pais_origen: '',
        incoterms: '',
        lugar_entrega: '',
        cantidad_unidades: '',
        termino_pago: '',
        tipo_moneda: '',
        precio: '',
        fecharealentrega_orden_detalle: '',
        fechaembarque_orden_detalle: '',
        fechaarribo_orden_detalle: ''
      });
    }
  }, [initialData, show]);

  // Manejo de cambios en los inputs (text, date, number)
  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    if (name && value !== undefined) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Manejo específico para el date picker principal
  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, fecha_entrega: date }));
  };

  // Manejo para los datepickers extras
  const handleExtraDateChange = (date, field) => {
    setFormData((prev) => ({ ...prev, [field]: date }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.descripcion.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'La descripción es obligatoria.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }

    // Estos campos corresponden a columnas NOT NULL con llave foránea en la
    // base de datos (id_tipo_moneda, id_tipo_despacho, id_tipo_embalaje,
    // id_intercom). Si se envían vacíos, el INSERT falla en MySQL.
    const requiredSelects = [
      { value: formData.tipo_embalaje, label: 'el tipo de embalaje' },
      { value: formData.tipo_despacho, label: 'el tipo de despacho' },
      { value: formData.incoterms, label: 'el incoterm' },
      { value: formData.tipo_moneda, label: 'el tipo de moneda' },
    ];

    const missing = requiredSelects.find(
      (f) => f.value === '' || f.value === null || f.value === undefined
    );

    if (missing) {
      Swal.fire({
        title: 'Error',
        text: `Debe seleccionar ${missing.label}.`,
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }

    return true;
  };

  // Función para transformar fechas a formato ISO
  const transformDate = (date) => {
    if (date && !(date instanceof Date)) {
      const parsed = new Date(date);
      if (!isNaN(parsed)) {
        return parsed.toISOString();
      }
    } else if (date instanceof Date) {
      return date.toISOString();
    }
    return "";
  };

  const onSave = () => {
    if (!validateForm()) return;

    const transformedData = {
      ...formData,
      fecha_entrega: transformDate(formData.fecha_entrega),
      fecharealentrega_orden_detalle: transformDate(formData.fecharealentrega_orden_detalle),
      fechaembarque_orden_detalle: transformDate(formData.fechaembarque_orden_detalle),
      fechaarribo_orden_detalle: transformDate(formData.fechaarribo_orden_detalle)
    };

    handleSave(transformedData);
  };

  return (
    <Modal
      aria-labelledby="contained-modal-title-vcenter"
      backdropClassName="modal-backdrop"
      className="modal-container"
      style={{ zIndex: 1050 }}
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{initialData ? 'Editar Embarque' : 'Agregar Embarque'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SoftBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Descripción
              </SoftTypography>
              <FormField
                type="text"
                placeholder="Ingrese la descripción"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
              />
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Fecha de entrega
              </SoftTypography>
              <SoftDatePicker
                input={{ placeholder: 'Seleccione' }}
                value={formData.fecha_entrega}
                onChange={handleDateChange}
              />
              <PackageSelector
                selectedPackage={formData.tipo_embalaje || ''}
                setSelectedPackage={(option) =>
                  handleSelectChange('tipo_embalaje', option?.value ?? '')
                }
              />
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Comprador
              </SoftTypography>
              <FormField
                type="text"
                placeholder="Ingrese el comprador"
                name="comprador"
                value={formData.comprador}
                onChange={handleInputChange}
              />
              <TransportSelector
                selectedTransport={formData.tipo_despacho || ''}
                setSelectedTransport={(value) => handleSelectChange('tipo_despacho', value)}
              />
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                País de origen
              </SoftTypography>
              <FormField
                type="text"
                placeholder="Ingrese el país"
                name="pais_origen"
                value={formData.pais_origen}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <IncotermSelector
                selectedIncoterm={formData.incoterms || ''}
                setSelectedIncoterm={(option) =>
                  handleSelectChange('incoterms', option?.value ?? '')
                }
              />
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Lugar de entrega
              </SoftTypography>
              <FormField
                type="text"
                placeholder="Ingrese el lugar de entrega"
                name="lugar_entrega"
                value={formData.lugar_entrega}
                onChange={handleInputChange}
              />
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Cantidad de unidades
              </SoftTypography>
              <FormField
                type="number"
                placeholder="Ingrese la cantidad"
                name="cantidad_unidades"
                value={formData.cantidad_unidades}
                onChange={handleInputChange}
              />
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Término de pago
              </SoftTypography>
              <FormField
                type="text"
                placeholder="Ingrese los términos de pago"
                name="termino_pago"
                value={formData.termino_pago}
                onChange={handleInputChange}
              />
              <CurrencySelector
                selectedCurrency={formData.tipo_moneda || ''}
                setSelectedCurrency={(option) =>
                  handleSelectChange('tipo_moneda', option?.value ?? '')
                }
              />
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Precio
              </SoftTypography>
              <FormField
                type="number"
                placeholder="Ingrese el precio"
                name="precio"
                value={formData.precio}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </SoftBox>
        {/* Mostrar inputs extras solo en modo edición */}
        {initialData && (
          <SoftBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Fecha real de entrega
                </SoftTypography>
                <SoftDatePicker
                  input={{ placeholder: 'Seleccione' }}
                  value={formData.fecharealentrega_orden_detalle}
                  onChange={(date) => handleExtraDateChange(date, 'fecharealentrega_orden_detalle')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Fecha de embarque
                </SoftTypography>
                <SoftDatePicker
                  input={{ placeholder: 'Seleccione' }}
                  value={formData.fechaembarque_orden_detalle}
                  onChange={(date) => handleExtraDateChange(date, 'fechaembarque_orden_detalle')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Fecha de arribo
                </SoftTypography>
                <SoftDatePicker
                  input={{ placeholder: 'Seleccione' }}
                  value={formData.fechaarribo_orden_detalle}
                  onChange={(date) => handleExtraDateChange(date, 'fechaarribo_orden_detalle')}
                />
              </Grid>
            </Grid>
          </SoftBox>
        )}
      </Modal.Body>
      <Modal.Footer>
        <SoftButton variant="gradient" color="success" onClick={onSave}>
          Guardar
        </SoftButton>
        <SoftButton variant="gradient" color="error" onClick={handleClose}>
          Cancelar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
};

ModalShipment.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  initialData: PropTypes.object,
};

export default ModalShipment;
